import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Paywall from "@/components/Paywall";

export default function TestPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [paywall, setPaywall] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setErr("");
    setPaywall(null);
    setTest(null);
    api
      .getTest(testId)
      .then(setTest)
      .catch((e) => {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 402 && typeof detail === "object") {
          setPaywall(detail);
        } else {
          setErr(
            (typeof detail === "string" ? detail : null) || "تعذّر تحميل الاختبار"
          );
        }
      });
  }, [testId]);

  if (paywall) {
    return (
      <Paywall
        trackId={paywall.track_id}
        productId={paywall.product_id}
        message={paywall.message}
        onBack={() => navigate("/tracks")}
      />
    );
  }

  if (err) {
    return (
      <div className="shell has-bottom-nav" data-testid="test-error">
        <div className="admin-error">{err}</div>
      </div>
    );
  }
  if (!test) {
    return (
      <div className="shell has-bottom-nav" data-testid="test-loading">
        <div className="admin-empty">جارٍ التحميل…</div>
      </div>
    );
  }

  const allAnswered = Object.keys(answers).length === test.questions.length;
  const setAns = (i, v) => setAnswers({ ...answers, [i]: v });

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const payload = test.questions.map((_, i) => answers[i] ?? null);
      const d = await api.submitTest(testId, payload);
      setResult(d);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && typeof detail === "object") {
        setPaywall(detail);
      } else {
        setErr(
          (typeof detail === "string" ? detail : null) || e.message || "فشل الإرسال"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -------- Result view --------
  if (result) {
    return (
      <div className="shell has-bottom-nav test-page" data-testid="test-result-page">
        <div className={`test-result ${result.passed ? "pass" : "fail"}`}
             data-testid="test-result">
          <div className="test-result-icon">{result.passed ? "🎉" : "📚"}</div>
          <h2>{result.passed ? "أحسنت! نجحتَ في الاختبار" : "حاول مجدّدًا"}</h2>
          <div className="test-result-score" data-testid="test-result-score">
            {result.score}%
          </div>
          <div className="test-result-meta">
            {result.correct} من {result.total} صحيحة · النجاح يتطلّب{" "}
            {result.passing_score}%
          </div>
          <div className="test-result-actions">
            {result.passed ? (
              <button className="btn btn-primary"
                      onClick={() => navigate("/tracks")}
                      data-testid="test-return-tracks">
                ← العودة إلى المسارات
              </button>
            ) : (
              <>
                <button className="btn btn-gold"
                        onClick={() => { setResult(null); setAnswers({}); }}
                        data-testid="test-retry">
                  🔄 إعادة المحاولة
                </button>
                <button className="btn btn-ghost"
                        onClick={() => navigate("/tracks")}>
                  العودة إلى المسارات
                </button>
              </>
            )}
          </div>
        </div>

        <div className="test-breakdown" data-testid="test-breakdown">
          <h3>تفاصيل الإجابات</h3>
          {result.breakdown.map((b, i) => (
            <div key={i} className={`test-q-result ${b.is_correct ? "ok" : "bad"}`}>
              <div className="test-q-head">
                <span className="test-q-mark">{b.is_correct ? "✓" : "✕"}</span>
                <div className="test-q-text">
                  <strong>س{i + 1}.</strong> {b.question}
                </div>
              </div>
              {!b.is_correct && (
                <div className="test-q-correct">
                  ✅ الإجابة الصحيحة:{" "}
                  {test.questions[i].options[b.correct_index]}
                </div>
              )}
              {b.explanation && (
                <div className="test-q-exp">💡 {b.explanation}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // -------- Quiz view --------
  return (
    <div className="shell has-bottom-nav test-page" data-testid="test-page">
      <div className="page-header test-header">
        <span className="test-badge">
          {test.kind === "final" ? "اختبار نهائيّ" : "اختبار وحدة"}
        </span>
        <h1 data-testid="test-title">{test.title}</h1>
        <p>{test.description}</p>
        <div className="test-meta-row">
          <span>📝 {test.question_count} أسئلة</span>
          <span>·</span>
          <span>🎯 النجاح: {test.passing_score}%</span>
          <span>·</span>
          <span data-testid="test-progress">
            {Object.keys(answers).length}/{test.questions.length} مُجاب
          </span>
        </div>
      </div>

      <div className="test-questions" data-testid="test-questions">
        {test.questions.map((q, i) => (
          <article key={i} className="test-q" data-testid={`q-${i}`}>
            <header>
              <span className="q-num">{i + 1}</span>
              <h4>{q.question}</h4>
            </header>
            <div className="q-options">
              {q.options.map((opt, j) => (
                <button
                  key={j}
                  className={`q-option ${answers[i] === j ? "active" : ""}`}
                  onClick={() => setAns(i, j)}
                  data-testid={`q-${i}-opt-${j}`}
                >
                  <span className="q-marker">
                    {["أ", "ب", "ج", "د", "هـ"][j] || j + 1}
                  </span>
                  <span>{opt}</span>
                </button>
              ))}
            </div>
          </article>
        ))}
      </div>

      {err && <div className="admin-error" data-testid="test-submit-err">{err}</div>}

      <div className="test-submit-bar">
        <button
          className="btn btn-primary cta-primary"
          onClick={() => allAnswered ? setConfirming(true) : null}
          disabled={!allAnswered || submitting}
          data-testid="test-submit-btn"
        >
          {submitting ? "...جارٍ الإرسال" :
            allAnswered ? "✓ تسليم الاختبار" :
            `أجِب على ${test.questions.length - Object.keys(answers).length} أسئلة المتبقية`}
        </button>
        <button className="btn btn-ghost" onClick={() => navigate("/tracks")}>
          إلغاء
        </button>
      </div>

      {confirming && (
        <div className="partnership-modal-backdrop" onClick={() => setConfirming(false)}>
          <div className="partnership-modal" onClick={(e) => e.stopPropagation()}
               data-testid="confirm-submit-modal" style={{ maxWidth: 420 }}>
            <h3>تأكيد التسليم</h3>
            <p className="dim">
              ستُسلِّم اختبارك الآن. هل أنت متأكّد؟
            </p>
            <div className="partnership-actions">
              <button className="btn btn-primary"
                      onClick={() => { setConfirming(false); submit(); }}
                      data-testid="confirm-submit-yes">
                نعم، سلِّم الآن
              </button>
              <button className="btn btn-ghost" onClick={() => setConfirming(false)}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
